import { useState } from 'react'
import type { StationSettings, StationWrite } from '../api/types'
import { stationIdFieldLabel } from '../api/types'
import { GATEWAY_NAME_NOTE, nameFollowsGateway } from '../lib/gateway'

interface Props {
  /** Absent when creating. */
  station?: StationSettings
  busy: boolean
  onSubmit: (body: StationWrite) => void
  onCancel: () => void
}

/** Create or edit a station.
 *
 *  On edit, only the fields actually changed are sent — the API is a PATCH
 *  and treats an absent field as untouched. Sending the whole form back would
 *  rewrite values nobody touched and fill the audit log's `from`/`changed`
 *  pairs with noise, which is the thing that makes the log readable.
 */
export function StationForm({ station, busy, onSubmit, onCancel }: Props) {
  const editing = station !== undefined
  const nameIsGateways = nameFollowsGateway(station?.vendor)
  const [form, setForm] = useState<Record<string, string>>(() => ({
    code: station?.code ?? '',
    name: station?.name ?? '',
    external_id: station?.external_id ?? '',
    timezone: station?.timezone ?? 'Asia/Tbilisi',
    poll_interval_s: String(station?.poll_interval_s ?? 600),
    schedule_time: (station?.schedule_time ?? '02:00').slice(0, 5),
    catchup_days: String(station?.catchup_days ?? 3),
    retry_attempts: String(station?.retry_attempts ?? 5),
    retry_delay_min: String(station?.retry_delay_min ?? 15),
    retention_start: station?.retention_start ?? '',
    latitude: station?.latitude == null ? '' : String(station.latitude),
    longitude: station?.longitude == null ? '' : String(station.longitude),
  }))
  const [scheduleEnabled, setScheduleEnabled] = useState(
    station?.schedule_enabled ?? false)

  const set = (key: string) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }))

  function submit() {
    const body: StationWrite = {}
    const numeric = ['poll_interval_s', 'catchup_days', 'retry_attempts',
      'retry_delay_min', 'latitude', 'longitude']

    for (const [key, value] of Object.entries(form)) {
      if (editing && key === 'code') continue
      if (editing && key === 'external_id') continue

      const trimmed = value.trim()
      // An empty optional clears the field on edit and is simply omitted on
      // create, where the API's own default is the right answer.
      //
      // Only when it is actually clearing something, though: sending null for
      // a field that was already null writes it into the audit log's
      // from/changed pair as if it were an edit. Three of those around one
      // real change is how a log stops being readable.
      if (trimmed === '') {
        const clearable = key === 'retention_start' || key === 'latitude'
          || key === 'longitude'
        const wasSet = (station as unknown as Record<string, unknown>)[key]
        if (editing && clearable && wasSet !== null && wasSet !== undefined) {
          ;(body as Record<string, unknown>)[key] = null
        }
        continue
      }

      const parsed = numeric.includes(key) ? Number(trimmed) : trimmed
      if (editing) {
        const before = (station as unknown as Record<string, unknown>)[key]
        const same = key === 'schedule_time'
          ? String(before).slice(0, 5) === trimmed
          : String(before ?? '') === String(parsed)
        if (same) continue
      }
      ;(body as Record<string, unknown>)[key] = parsed
    }

    if (!editing || scheduleEnabled !== station.schedule_enabled) {
      body.schedule_enabled = scheduleEnabled
    }
    onSubmit(body)
  }

  return (
    <div className="form">
      <div className="form-grid">
        <label className="field">
          <span>Code</span>
          <input value={form.code} onChange={set('code')} disabled={editing}
                 placeholder="rq-lajanuri" />
          {editing && <small className="muted">
            Identity: a station needing a different code is a different
            station.
          </small>}
        </label>

        <label className="field">
          <span>{stationIdFieldLabel(station?.vendor)}</span>
          <input value={form.external_id} onChange={set('external_id')}
                 disabled={editing} placeholder="08252104" />
          {editing && <small className="muted">
            Identity: repointing this while keeping the measurements would
            merge two archives into one series.
          </small>}
        </label>

        <label className="field">
          <span>Name</span>
          <input value={form.name} onChange={set('name')}
                 disabled={nameIsGateways} />
          {nameIsGateways && <small className="muted">{GATEWAY_NAME_NOTE}</small>}
        </label>

        <label className="field">
          <span>Timezone</span>
          <input value={form.timezone} onChange={set('timezone')} />
        </label>

        <label className="field">
          <span>Logger interval (seconds)</span>
          <input value={form.poll_interval_s} onChange={set('poll_interval_s')}
                 inputMode="numeric" />
          <small className="muted">
            How often the logger records, measured by the probe, not chosen.
            Completeness is judged against it for the whole archive.
          </small>
        </label>

        <label className="field">
          <span>Collect daily at</span>
          <input type="time" value={form.schedule_time}
                 onChange={set('schedule_time')} />
          <small className="muted">Station local. Unrelated to the interval.</small>
        </label>

        <label className="field">
          <span>Catch-up days</span>
          <input value={form.catchup_days} onChange={set('catchup_days')}
                 inputMode="numeric" />
          <small className="muted">
            Refetched on every run to pick up late data. Still one API call.
          </small>
        </label>

        <label className="field">
          <span>Retention start</span>
          <input type="date" value={form.retention_start}
                 onChange={set('retention_start')} />
          <small className="muted">
            Earliest date MDS will serve. Earlier windows return
            "data expired".
          </small>
        </label>

        <label className="field">
          <span>Retry attempts</span>
          <input value={form.retry_attempts} onChange={set('retry_attempts')}
                 inputMode="numeric" />
        </label>

        <label className="field">
          <span>Retry delay (minutes)</span>
          <input value={form.retry_delay_min} onChange={set('retry_delay_min')}
                 inputMode="numeric" />
        </label>

        <label className="field">
          <span>Latitude</span>
          <input value={form.latitude} onChange={set('latitude')}
                 inputMode="decimal" />
        </label>

        <label className="field">
          <span>Longitude</span>
          <input value={form.longitude} onChange={set('longitude')}
                 inputMode="decimal" />
        </label>
      </div>

      <label className="check">
        <input type="checkbox" checked={scheduleEnabled}
               onChange={(e) => setScheduleEnabled(e.target.checked)} />
        <span>Collect on a schedule</span>
      </label>
      {!editing && (
        <small className="muted">
          Left off for a new station: it has no channels mapped yet, so a run
          would store nothing, and a mistyped MDS id is retried every tick.
          Map the channels, then turn this on.
        </small>
      )}

      <div className="form-actions">
        <button className="button primary" onClick={submit} disabled={busy}>
          {busy ? 'Saving…' : editing ? 'Save changes' : 'Create station'}
        </button>
        <button className="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  )
}
